import React, { useContext, useState } from 'react';
import { AppContext, supabase, SUPABASE_URL } from '../contexts/AppContext';
import { useUI } from '../contexts/UIContext';
import { compressImage } from '../utils/image';
import { uploadAvatar } from '../utils/upload';

const ProfileScreen: React.FC = () => {
  const { user, updateUserProfile } = useContext(AppContext);
  const ui = useUI();

  const [nombre, setNombre] = useState(user?.nombre || '');
  const [email, setEmail] = useState(user?.email || '');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState<number>(0);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  // using shared upload helper (provides progress via callback)
  if (!user) return <div>No autenticado.</div>;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const f = e.target.files[0];
      // Basic validation
      if (!f.type.startsWith('image/')) {
        ui.showToast('error', 'Por favor seleccioná un archivo de imagen');
        return;
      }
      const maxBytes = 5 * 1024 * 1024; // allow up to 5MB to let compression run client-side
      if (f.size > maxBytes) {
        ui.showToast('error', 'Imagen muy grande (máx 5MB). Elegí otra o reducí su tamaño.');
        return;
      }

      setAvatarFile(f);
      try {
        const url = URL.createObjectURL(f);
        setAvatarPreview(url);
      } catch (err) {
        setAvatarPreview(undefined as any);
      }
    }
  };

  // compressImage helper moved to utils/image

  React.useEffect(() => {
    return () => {
      if (avatarPreview) {
        try { URL.revokeObjectURL(avatarPreview); } catch (e) {}
      }
    };
  }, [avatarPreview]);

  const triggerFileSelect = () => fileInputRef.current?.click();

  const removeSelectedAvatar = () => {
    setAvatarFile(null);
    if (avatarPreview) {
      try { URL.revokeObjectURL(avatarPreview); } catch (e) {}
    }
    setAvatarPreview(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let avatar_url = user.avatar_url;

      if (avatarFile) {
        setUploading(true);
        setUploadPercent(0);
        const fileExt = avatarFile.name.split('.').pop();
        // Do not prefix the bucket name in the path -- Supabase storage already targets the bucket.
        const filePath = `${user.id}/${Date.now()}.${fileExt}`;

        // Debug logs: session + upload options
        try {
          const sessionRes = await supabase.auth.getSession();
          console.log('DEBUG: supabase session', sessionRes?.data?.session || sessionRes);
        } catch (e) {
          console.warn('DEBUG: could not read session', e);
        }

        const uploadOptions = { upsert: true, metadata: { user_id: user.id }, contentType: avatarFile.type };
        console.log('DEBUG: uploading avatar', { filePath, uploadOptions, fileName: avatarFile.name });

        // Try compressing large images client-side to reduce upload time
        let fileToUpload: File = avatarFile;
        try {
          if (avatarFile.size > 1024 * 1024) {
            const compressed = await compressImage(avatarFile, 1024, 1024, 0.8);
            if (compressed) fileToUpload = compressed;
          }
        } catch (e) {
          console.warn('Image compression failed, continuing with original file', e);
        }

        const result = await uploadAvatar(fileToUpload, (p) => setUploadPercent(p));
        if (!result.success) {
          console.error('Profile save error: upload failed', result);
          throw new Error('No se pudo subir el avatar');
        }

        // The Edge Function returns the actual key/publicUrl it created.
        // Use that returned value instead of assuming the client-side `filePath` matches.
        const returned = result.data || {};
        if (returned.publicUrl) {
          avatar_url = returned.publicUrl;
        } else if (returned.key) {
          const { data } = supabase.storage.from('avatars').getPublicUrl(returned.key);
          avatar_url = data?.publicUrl || avatar_url;
        } else if (returned.data && typeof returned.data === 'string') {
          // fallback: if parsed differently
          try {
            const maybe = JSON.parse(returned.data);
            avatar_url = maybe.publicUrl || avatar_url;
          } catch (e) {
            // ignore
          }
        } else {
          // last resort: try the original filePath
          const { data } = supabase.storage.from('avatars').getPublicUrl(filePath);
          avatar_url = data?.publicUrl || avatar_url;
        }
      }

      const ok = await updateUserProfile({ nombre: nombre.trim(), email: email.trim(), avatar_url });
      if (ok) {
        ui.showToast('success', 'Perfil actualizado');
      } else {
        ui.showToast('error', 'No se pudo actualizar el perfil');
      }
    } catch (err: any) {
      console.error('Profile save error:', err);
      ui.showToast('error', err.message || 'Error al subir avatar');
    } finally {
      setUploading(false);
      setUploadPercent(0);
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow">
      <h2 className="text-2xl font-bold mb-6">Mi Perfil</h2>
      <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        <div className="col-span-1 flex flex-col items-center md:items-start">
          <p className="text-sm text-gray-600 mb-3">Avatar</p>
          <div className="relative">
            <div className="w-32 h-32 bg-gray-100 rounded-full overflow-hidden flex items-center justify-center border">
              {avatarPreview ? (
                // preview of newly selected file
                // eslint-disable-next-line @next/next/no-img-element
                <img src={avatarPreview} alt="avatar preview" className="w-full h-full object-cover" />
              ) : user.avatar_url ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={user.avatar_url} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="text-gray-400">Sin avatar</span>
              )}
            </div>

            <div className="flex gap-2 mt-3">
              <button type="button" onClick={triggerFileSelect} className="px-3 py-1 bg-white border rounded text-sm hover:bg-gray-50">
                Cambiar avatar
              </button>
              {avatarPreview && (
                <button type="button" onClick={removeSelectedAvatar} className="px-3 py-1 bg-red-50 border border-red-200 rounded text-sm text-red-600 hover:bg-red-100">
                  Quitar
                </button>
              )}
            </div>

            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
          </div>

          <p className="text-xs text-gray-500 mt-3">Formato JPG/PNG. Máx 2MB.</p>
        </div>

        <div className="col-span-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nombre</label>
              <input value={nombre} onChange={e => setNombre(e.target.value)} className="mt-1 block w-full border rounded-md p-2" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="mt-1 block w-full border rounded-md p-2" />
            </div>
          </div>

          <div className="mt-6 flex flex-col items-end gap-3">
            {uploading && (
              <div className="w-full md:w-2/3">
                <div className="h-2 bg-gray-100 rounded overflow-hidden">
                  <div style={{ width: `${uploadPercent}%` }} className="h-full bg-blue-600 transition-all" />
                </div>
                <div className="text-xs text-gray-600 mt-1">Subiendo imagen: {uploadPercent}%</div>
              </div>
            )}

            <div className="w-full md:w-auto flex justify-end">
              <button type="submit" disabled={uploading} className="px-5 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700">
                {uploading ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProfileScreen;
